import { NumField, TextField, Field } from "@/components/Fields";
import { setGridCount, scaleParts } from "@/lib/geometry";
import { TypologyBox, FaceWidths } from "@/components/TypologyBox";
import { RefineBox } from "@/components/RefineBox";
import { IwinBox } from "@/components/IwinBox";
import { CornersBox } from "@/components/CornersBox";

const PartInputs = ({ label, parts, onChange, prefix }) => (
  <Field label={label}>
    <div className="grid grid-cols-3 gap-2">
      {parts.map((v, i) => (
        <input key={i} data-testid={`${prefix}-${i}`} type="number" min={1} value={v}
          onChange={(e) => onChange(i, Math.max(1, parseInt(e.target.value, 10) || 1))}
          className="h-9 px-2 rounded-md bg-[#0B0F19] border border-slate-700 font-mono text-sm text-slate-100 focus:outline-none focus:border-cyan-500" />
      ))}
    </div>
  </Field>
);

const Stepper = ({ label, value, onChange, testid }) => (
  <Field label={label}>
    <div className="flex items-center h-9 rounded-md border border-slate-700 bg-[#0B0F19]">
      <button data-testid={`${testid}-dec`} onClick={() => onChange(value - 1)} className="w-9 h-full text-slate-300 hover:text-cyan-300">−</button>
      <span data-testid={testid} className="flex-1 text-center font-mono text-sm">{value}</span>
      <button data-testid={`${testid}-inc`} onClick={() => onChange(value + 1)} className="w-9 h-full text-slate-300 hover:text-cyan-300">+</button>
    </div>
  </Field>
);

export const GeometryTab = ({ item, update, applyFaceToAll, projectId, hasPdf, items }) => {
  const set = (k) => (v) => update({ ...item, [k]: v });
  const setTotal = (k, partsKey) => (v) => {
    const n = typeof v === "number" && v > 0 ? v : v;
    update({ ...item, [k]: n, ...(n > 0 ? { [partsKey]: scaleParts(item[partsKey], n) } : {}) });
  };
  const setPart = (partsKey, totalKey) => (i, v) => {
    const parts = item[partsKey].map((p, j) => (j === i ? v : p));
    update({ ...item, [partsKey]: parts, [totalKey]: parts.reduce((a, b) => a + b, 0) });
  };
  return (
    <div className="space-y-4">
      <TypologyBox item={item} update={update} />
      <IwinBox item={item} update={update} />
      <RefineBox item={item} update={update} projectId={projectId} hasPdf={hasPdf} />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Tag" value={item.tag} onChange={set("tag")} testid="tag-input" />
        <Field label="Type">
          <div className="grid grid-cols-2 h-9 rounded-md border border-slate-700 overflow-hidden">
            {["window", "door"].map((t) => (
              <button key={t} data-testid={`type-${t}-btn`} onClick={() => set("type")(t)}
                className={`text-xs font-mono uppercase transition-colors ${item.type === t ? "bg-cyan-500 text-slate-950" : "text-slate-400 hover:bg-slate-800"}`}>{t}</button>
            ))}
          </div>
        </Field>
      </div>
      <TextField label="Description" value={item.name} onChange={set("name")} testid="name-input" />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Location" value={item.location} onChange={set("location")} testid="location-input" />
        <NumField label="Quantity" value={item.quantity} onChange={set("quantity")} testid="quantity-input" />
        <NumField label="Overall width (mm)" value={item.width} onChange={setTotal("width", "columns")} testid="width-input" min={200} />
        <NumField label="Overall height (mm)" value={item.height} onChange={setTotal("height", "rows")} testid="height-input" min={200} />
      </div>
      <FaceWidths item={item} update={update} applyFaceToAll={applyFaceToAll} />
      <div className="h-px bg-slate-800" />
      <div className="grid grid-cols-2 gap-3">
        <Stepper label="Vertical divisions" value={item.columns.length} testid="columns-count" onChange={(n) => update(setGridCount(item, "col", n))} />
        <Stepper label="Horizontal divisions" value={item.rows.length} testid="rows-count" onChange={(n) => update(setGridCount(item, "row", n))} />
      </div>
      <PartInputs label="Column widths L→R (mm)" parts={item.columns} prefix="column-width" onChange={setPart("columns", "width")} />
      <PartInputs label="Row heights top→bottom (mm)" parts={item.rows} prefix="row-height" onChange={setPart("rows", "height")} />
      <CornersBox item={item} update={update} items={items} />
    </div>
  );
};
